import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import {
  api,
  ApiError,
  sendMessage,
  type ChatMode,
  type CloneInfo,
  type Conversation,
  type Message,
  type Version,
} from "../api";
import { Markdown } from "./Markdown";

interface Props {
  slug: string;
  /** Document the learner is viewing; "ask" answers are grounded in it. */
  viewing: Version;
  cloneExists: boolean;
  onCloneChanged: (clone: CloneInfo, changed: boolean) => void;
  onFaqChanged: () => void;
}

interface Streaming {
  mode: ChatMode;
  text: string;
  tool: string | null;
}

const ASK_SUGGESTIONS = [
  "Summarize this course in five bullet points.",
  "How should I structure CLAUDE.md for a monorepo?",
  "When should I use a skill vs. a subagent vs. a hook?",
];
const EDIT_SUGGESTIONS = [
  "Add a short “My team's conventions” subsection to the memory section with placeholders I can fill in.",
  "Rewrite the Windows tip in the installation section to recommend WSL for Linux-first projects.",
];

export function ChatPanel({ slug, viewing, cloneExists, onCloneChanged, onFaqChanged }: Props) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [pendingUser, setPendingUser] = useState<string | null>(null);
  const [streaming, setStreaming] = useState<Streaming | null>(null);
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<ChatMode>("ask");
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  // A conversation created by send() is already being filled by the stream;
  // loading it from the server at the same time would race with that.
  const skipLoadRef = useRef<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const busy = streaming !== null;

  const loadConversations = useCallback(async () => {
    const list = await api.conversations(slug);
    setConversations(list);
    return list;
  }, [slug]);

  // Initial load: open the most recent conversation.
  useEffect(() => {
    let cancelled = false;
    loadConversations()
      .then((list) => {
        if (!cancelled) setActiveId(list[0]?.id ?? null);
      })
      .catch((e) => setError(e.message));
    return () => {
      cancelled = true;
      abortRef.current?.abort();
    };
  }, [loadConversations]);

  useEffect(() => {
    setConfirmDelete(false);
    if (activeId === null) {
      setMessages([]);
      return;
    }
    if (skipLoadRef.current === activeId) {
      skipLoadRef.current = null;
      return;
    }
    let cancelled = false;
    api
      .conversation(activeId)
      .then((c) => !cancelled && setMessages(c.messages))
      .catch((e) => setError(e.message));
    return () => {
      cancelled = true;
    };
  }, [activeId]);

  // Keep the newest content in view while streaming.
  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, streaming, pendingUser]);

  async function send(text: string) {
    const content = text.trim();
    if (!content || busy) return;
    setError(null);
    setInput("");
    setPendingUser(content);
    setStreaming({ mode, text: "", tool: null });
    const controller = new AbortController();
    abortRef.current = controller;

    let convId = activeId;
    let started = false; // the server accepted (and stored) the learner's message
    try {
      if (convId === null) {
        const conv = await api.createConversation(slug);
        convId = conv.id;
        skipLoadRef.current = conv.id;
        setConversations((prev) => [conv, ...prev]);
        setActiveId(conv.id);
      }
      await sendMessage(
        convId,
        { content, mode, context: viewing },
        {
          onStart: (userMessage) => {
            started = true;
            setPendingUser(null);
            setMessages((prev) => [...prev, userMessage]);
          },
          onDelta: (t) => setStreaming((s) => (s ? { ...s, text: s.text + t, tool: null } : s)),
          onTool: (t) => setStreaming((s) => (s ? { ...s, tool: t } : s)),
          onDone: ({ message, clone, clone_changed }) => {
            setMessages((prev) => [...prev, message]);
            if (clone) onCloneChanged(clone, clone_changed);
          },
        },
        controller.signal,
      );
      void loadConversations(); // title / ordering may have changed
    } catch (e) {
      if (controller.signal.aborted) {
        // The server stores the interrupted answer (and rolls back a half-done edit); show its version.
        if (convId !== null) {
          setTimeout(() => {
            void api.conversation(convId!).then((c) => setMessages(c.messages));
            void api.course(slug).then((d) => onCloneChanged(d.clone, false));
          }, 400);
        }
      } else {
        setError(e instanceof ApiError || e instanceof Error ? e.message : "Something went wrong");
        if (!started) setInput(content); // nothing was stored; give the text back
      }
    } finally {
      setPendingUser(null);
      setStreaming(null);
      abortRef.current = null;
      inputRef.current?.focus();
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void send(input);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      void send(input);
    }
  }

  async function saveToFaq(message: Message) {
    try {
      const entry = await api.saveToFaq(slug, message.id);
      setMessages((prev) => prev.map((m) => (m.id === message.id ? { ...m, faq_entry_id: entry.id } : m)));
      onFaqChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not save to FAQ");
    }
  }

  async function deleteConversation() {
    if (activeId === null) return;
    try {
      await api.deleteConversation(activeId);
      const list = await loadConversations();
      setActiveId(list[0]?.id ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Could not delete");
    }
  }

  const suggestions = mode === "edit" ? EDIT_SUGGESTIONS : ASK_SUGGESTIONS;
  const empty = messages.length === 0 && !pendingUser && !streaming;

  return (
    <section className="chat" aria-label="Course assistant">
      <div className="chat-toolbar">
        <select
          className="conv-select"
          aria-label="Conversation"
          value={activeId ?? ""}
          onChange={(e) => setActiveId(e.target.value ? Number(e.target.value) : null)}
          disabled={busy}
        >
          {activeId === null && <option value="">New conversation</option>}
          {conversations.map((c) => (
            <option key={c.id} value={c.id}>
              {c.title}
            </option>
          ))}
        </select>
        <button className="btn btn-sm" onClick={() => setActiveId(null)} disabled={busy || activeId === null}>
          New chat
        </button>
        {activeId !== null &&
          (confirmDelete ? (
            <button className="btn btn-sm btn-danger" onClick={() => void deleteConversation()} disabled={busy}>
              Confirm
            </button>
          ) : (
            <button
              className="btn btn-sm btn-ghost"
              title="Delete this conversation"
              onClick={() => setConfirmDelete(true)}
              disabled={busy}
            >
              Delete
            </button>
          ))}
      </div>

      <div className="chat-messages" ref={listRef} aria-live="polite">
        {empty && (
          <div className="chat-empty">
            <p>
              {mode === "edit"
                ? "Describe a change and Claude will make it in your own copy of the course. The original is never touched."
                : "Ask anything about the course. Answers are grounded in the document you're reading."}
            </p>
            <div className="suggestions">
              {suggestions.map((s) => (
                <button key={s} className="suggestion" onClick={() => void send(s)}>
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((m) => (
          <MessageBubble key={m.id} message={m} onSave={() => void saveToFaq(m)} />
        ))}

        {pendingUser && (
          <div className="msg msg-user">
            <div className="msg-body">{pendingUser}</div>
          </div>
        )}

        {streaming && (
          <div className="msg msg-assistant streaming">
            {streaming.mode === "edit" && <span className="badge">Editing your copy</span>}
            {streaming.text ? (
              <Markdown text={streaming.text} className="msg-body" />
            ) : (
              <div className="msg-body thinking">
                <span className="dots" aria-label="Thinking">
                  <i />
                  <i />
                  <i />
                </span>
              </div>
            )}
            {streaming.tool && <div className="tool-activity">{streaming.tool}…</div>}
          </div>
        )}
      </div>

      {error && (
        <div className="chat-error" role="alert">
          {error}
          <button className="link-btn" onClick={() => setError(null)}>
            Dismiss
          </button>
        </div>
      )}

      <form className={mode === "edit" ? "composer edit-mode" : "composer"} onSubmit={onSubmit}>
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={mode === "edit" ? "Describe the change to make in your copy…" : "Ask about the course…"}
          rows={3}
          maxLength={8000}
          aria-label="Message"
        />
        <div className="composer-row">
          <label className="switch" title="When on, Claude edits your personal copy of the course instead of answering in chat">
            <input
              type="checkbox"
              checked={mode === "edit"}
              onChange={(e) => setMode(e.target.checked ? "edit" : "ask")}
              disabled={busy}
            />
            <span className="switch-track" aria-hidden />
            <span>Edit my copy</span>
          </label>
          {mode === "edit" && !cloneExists && <span className="hint">Your first edit creates your copy.</span>}
          <span className="spacer" />
          {busy ? (
            <button type="button" className="btn btn-sm" onClick={() => abortRef.current?.abort()}>
              Stop
            </button>
          ) : (
            <button className="btn btn-primary btn-sm" disabled={!input.trim()}>
              {mode === "edit" ? "Apply edit" : "Send"}
            </button>
          )}
        </div>
      </form>
    </section>
  );
}

function MessageBubble({ message, onSave }: { message: Message; onSave: () => void }) {
  const [copied, setCopied] = useState(false);
  if (message.role === "user") {
    return (
      <div className="msg msg-user">
        {message.mode === "edit" && <span className="badge badge-light">Edit request</span>}
        <div className="msg-body">{message.content}</div>
      </div>
    );
  }
  const failed = message.status === "error";
  return (
    <div className={failed ? "msg msg-assistant failed" : "msg msg-assistant"}>
      {message.mode === "edit" && !failed && <span className="badge">Edited your copy</span>}
      <Markdown text={message.content} className="msg-body" />
      {!failed && (
        <div className="msg-actions">
          {message.faq_entry_id ? (
            <span className="saved">✓ Saved to FAQ</span>
          ) : (
            <button className="link-btn" onClick={onSave}>
              Save to FAQ
            </button>
          )}
          <button
            className="link-btn"
            onClick={() => {
              void navigator.clipboard?.writeText(message.content).then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 1500);
              });
            }}
          >
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      )}
    </div>
  );
}
